import { Link, useParams } from 'react-router-dom';
import { ButtonLink } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { useAppBasePath } from '../../_shared/base-path';
import { useLinkedInCampaign } from '../api-client';
import { costPer, count, metaResults, pct, usd } from '../format';
import type { LinkedInCampaignDetail } from '../types';
import { CampaignAsk } from './CampaignAsk';
import { MetaStatusBadge } from './CampaignStatusBadge';
import { CplChart } from './CplChart';
import { Kpi, Panel } from './Panels';

function LinkedInCampaignDetailView({ detail }: { detail: LinkedInCampaignDetail }) {
  const base = useAppBasePath();
  const { campaign: c, daily, creatives } = detail;
  const results = metaResults(c.metrics);
  const dailyCost = daily.map(d => {
    const r = metaResults(d).value;
    return r > 0 ? Math.round((d.spendUsd / r) * 100) / 100 : 0;
  });

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <Link to={`${base}/performance`} className="text-xs text-content-tertiary hover:underline">
            Performance
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <Heading level={1} size="lg">
              {c.name}
            </Heading>
            <MetaStatusBadge effectiveStatus={c.status} />
            <Text size="sm" variant="tertiary">
              LinkedIn · {c.groupName ?? 'No group'} · {c.objective.toLowerCase().replace(/_/g, ' ')}
            </Text>
          </div>
        </div>
        <ButtonLink variant="secondary" href={detail.campaignManagerUrl} target="_blank" rel="noopener noreferrer" className="ml-auto">
          Open in Campaign Manager
        </ButtonLink>
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi
              label="Spend"
              value={usd(c.metrics.spendUsd, 2)}
              sub={c.dailyBudgetUsd === null ? 'No daily budget' : `${usd(c.dailyBudgetUsd)}/day budget`}
            />
            <Kpi label="Impressions" value={count(c.metrics.impressions)} sub="Lifetime" />
            <Kpi
              label="Clicks"
              value={count(c.metrics.clicks)}
              sub={c.metrics.impressions > 0 ? `${pct(c.metrics.clicks / c.metrics.impressions)} CTR` : 'No impressions yet'}
            />
            <Kpi label="Results" value={count(results.value)} sub={results.label} />
            <Kpi label="Cost per result" value={costPer(c.metrics.spendUsd, results.value)} sub="Lifetime" />
          </div>

          <Panel className="flex flex-col gap-3">
            <Heading level={2} size="sm">
              Daily cost per result
            </Heading>
            {daily.length > 0 ? (
              <CplChart platform="LinkedIn" dailyCpl={dailyCost} labels={daily.map(d => d.date.slice(5))} />
            ) : (
              <Text size="sm" variant="tertiary">
                No delivery yet. Numbers appear here once the campaign is active and spending.
              </Text>
            )}
          </Panel>

          <div className="flex flex-col gap-3">
            <Heading level={2} size="sm">
              Ads
            </Heading>
            {creatives.length === 0 ? (
              <Text size="sm" variant="tertiary">
                This campaign has no ads yet.
              </Text>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Ad</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Boosted post</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creatives.map(cr => (
                    <TableRow key={cr.id}>
                      <TableCell>{cr.id}</TableCell>
                      <TableCell>
                        <MetaStatusBadge effectiveStatus={cr.status} />
                      </TableCell>
                      <TableCell>
                        {cr.postUrn ? (
                          <a
                            href={`https://www.linkedin.com/feed/update/${cr.postUrn}/`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            Open post
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
        <aside>
          <CampaignAsk platform="linkedin" campaignId={c.id} />
        </aside>
      </div>
    </section>
  );
}

export default function LinkedInCampaignDetailPage() {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const base = useAppBasePath();
  const detail = useLinkedInCampaign(campaignId);

  if (detail.isLoading) return <Text variant="tertiary">Loading campaign…</Text>;
  if (!detail.data) {
    return (
      <div className="flex flex-col gap-2">
        <Text variant="tertiary">This campaign couldn't be loaded from LinkedIn.</Text>
        <Link to={`${base}/performance`} className="text-sm hover:underline">
          Back to performance
        </Link>
      </div>
    );
  }
  return <LinkedInCampaignDetailView key={campaignId} detail={detail.data} />;
}
